import { computed, provide, inject, Ref } from "vue";
import MarketSupports from "../lib/marketSupports";
import {
  HANDICAP_BETTING,
  MATCH_WINNER,
  OVER_UNDER_TOTALS,
  FTR_GROUP,
  WW_GROUP,
  WDW_GROUP,
  LIST_GROUP,
  PROMO_GROUP,
  MAIN_MARKET_GROUP,
  FULL_TIME_RESULT,
  INSIGHT_GROUP
} from "../constants/market-types";
import { BetData, Market, NormalizedEntity } from "@fresh8/types/src/betData";
import { SportEvent } from "@fresh8/types/src/betData";
import { CardTypes } from "@fresh8/types/src";
import { Sport, SportEnum } from "@fresh8/types/src/language";
interface EventMarketGroups {
  [PROMO_GROUP]?: string[];
  [FTR_GROUP]?: string[];
  [LIST_GROUP]?: string[];
  [MAIN_MARKET_GROUP]?: string[];
  [INSIGHT_GROUP]?: string[];
  [WDW_GROUP]?: string[];
  [WW_GROUP]?: string[];
}

const MAIN_MARKET_SPORTS = [
  "Basketball",
  "Baseball",
  "American Football",
  "Ice Hockey"
];

const marketSupports = MarketSupports();
type Markets = Record<string, Market>;

const DEFAULT_EVENT_CARD_ORDER: CardTypes[] = [
  "promo",
  "mainMarket",
  "insight",
  "wdw",
  "ww",
  "list"
];
/**
 * Method to group all the marketIds for an event by their normalised market type.
 * @param {string[]} marketIds
 */
const getMarketsGroupedByType = (
  marketIds: string[],
  markets: Markets
): EventMarketGroups => {
  return marketIds.reduce((groupAccumulator, currentID) => {
    const marketType = markets[currentID].marketType;
    const isPriorityMarket = markets[currentID].priorityMarket;
    const marketTypeGroup = marketSupports[marketType];

    // let accumulatorGroup: string[] = [];
    let accumulatorGroup: string[] = groupAccumulator[marketTypeGroup] || [];

    if (groupAccumulator[marketTypeGroup]) {
      // make sure the priority market is always first in the marketTypeGroup
      // this is used so we can target specific promo options via the campaigns.
      if (isPriorityMarket) {
        // accumulatorGroup = [currentID, ...groupAccumulator[marketTypeGroup]];
        accumulatorGroup.unshift(currentID);
      } else {
        // accumulatorGroup = [...groupAccumulator[marketTypeGroup], currentID];
        accumulatorGroup.push(currentID);
      }
    } else {
      accumulatorGroup = [currentID];
    }

    groupAccumulator[marketTypeGroup] = accumulatorGroup;

    return groupAccumulator;
  }, {});
};

/**
 * method to pull our the market ids that only match the main market types from the FTR market type group.
 * @param {string[]} ftrMarkets
 */
const getMainMarkets = (
  ftrMarkets: string[] | undefined,
  markets: Markets,
  sport: string
): string[] => {
  if (!ftrMarkets || !MAIN_MARKET_SPORTS.includes(sport)) {
    return [];
  }

  const mainMarkets = ftrMarkets.reduce(
    (mainMarketAccumulator, currentID) => {
      const currentMarketType = markets[currentID].marketType;
      // only map current market type id if an id hasn't been mapped already for that market type
      if (mainMarketAccumulator[currentMarketType] === "") {
        mainMarketAccumulator[currentMarketType] = currentID;
      }
      return mainMarketAccumulator;
    },
    {
      [HANDICAP_BETTING]: "",
      [MATCH_WINNER]: "",
      [OVER_UNDER_TOTALS]: ""
    }
  );
  // removing markets that haven't got ids.
  return Object.values(mainMarkets).filter(market => market !== "");
};

const generateWdwParlayReducer = (
  markets: Record<string, Market>
): ((accumulator: string[], currentValue: SportEvent) => string[]) => {
  return (accumulator: string[], currentValue: SportEvent) => {
    const matchedMarket = currentValue.markets.find(marketId => {
      const currentMarketType = markets[marketId].marketType;

      return currentMarketType === FULL_TIME_RESULT;
    });

    if (matchedMarket) {
      accumulator.push(matchedMarket);
    }

    return accumulator;
  };
};

const generateWwParlayReducer = (
  markets: Record<string, Market>
): ((accumulator: string[], currentValue: SportEvent) => string[]) => {
  return (accumulator: string[], currentValue: SportEvent) => {
    const matchedMarket = currentValue.markets.find(marketId => {
      const currentMarketType = markets[marketId].marketType;

      return currentMarketType === MATCH_WINNER;
    });

    if (matchedMarket) {
      accumulator.push(matchedMarket);
    }

    return accumulator;
  };
};

/**
 * Checks if any of the markets have any insights
 * if there are market insights MarketInsight and MarketInsightScore will no longer be optional
 * in current scope
 */

function hasInsights(markets: Market[]): markets is MarketWithInsight[] {
  return markets.some(market => {
    if (!market.marketInsight) false;
    return market.marketInsight && market.marketInsight.length > 0;
  });
}

type MarketWithInsight = Market & {
  marketInsightScore: number;
  marketInsight: string;
};

/**
 * reduce down to market which contains the highest scoring insight
 */

function insightReducer(
  highest: MarketWithInsight,
  cur: MarketWithInsight
): MarketWithInsight {
  return cur.marketInsightScore > highest.marketInsightScore ? cur : highest;
}

/**
 * Find the market with the highest scoring insight,
 * if market has no insight return undefined (doesnt render insight card)
 */

function findMarketInsight(markets: Market[]): string[] | undefined {
  if (hasInsights(markets)) {
    // reduce markets down to the single market that has the highest marketInsightScore
    const marketInsight = markets.reduce(insightReducer);

    // threshold for 'relevent' insight is a score of 60, if heightest scoring insight is below threshold do not render insight
    if (marketInsight.marketInsightScore < 60) return undefined;

    return [marketInsight.marketId];
  }
  return undefined;
}

const hasDrawOption = (eventMarket: Market) => {
  return eventMarket.options.length > 2;
};

const getAllSportsFromEvents = (
  events: NormalizedEntity<SportEvent>
): Sport[] | [] => {
  return events ? Object.values(events).map(event => event.sport) : [];
};

const getEventCountPerSport = (
  allSportsFromEvents: [] | Sport[]
): Record<string, number> => {
  const uniqueValues = {};

  // We count how many events per sport we have
  // Remember we get 4 events in total per AdTag
  allSportsFromEvents.forEach(value => {
    uniqueValues[value] =
      uniqueValues[value] === undefined ? 1 : (uniqueValues[value] += 1);
  });

  return uniqueValues;
};

const getShouldCheckSport = (
  events: NormalizedEntity<SportEvent>,
  markets: Record<string, Market>
): boolean => {
  let shouldCheckSport = false;
  const NUM_OF_MARKETS_TO_MATCH = 2;

  // We wanna see whether any match has those two markets
  // and if so attach some additional logic later on
  events &&
    Object.values(events).forEach(event => {
      let numOfMatchedMarkets = 0;

      event.markets.forEach(market => {
        if (
          markets[market].marketType === FULL_TIME_RESULT ||
          markets[market].marketType === MATCH_WINNER
        ) {
          numOfMatchedMarkets++;
        }
      });

      if (numOfMatchedMarkets === NUM_OF_MARKETS_TO_MATCH) {
        shouldCheckSport = true;
      }
    });

  return shouldCheckSport;
};

const getParlayReducerGenerator = (
  events: NormalizedEntity<SportEvent>,
  markets: Record<string, Market>,
  eventCountPerSport: Record<string, number>
): ((
  markets: Record<string, Market>
) => (accumulator: string[], currentValue: SportEvent) => string[]) => {
  const shouldCheckSport = getShouldCheckSport(events, markets);
  let generateParlayReducer = generateWdwParlayReducer;
  const TOO_MANY_SPORTS_FOR_PARLAY_CARD = 3;
  const NUM_OF_MATCHES_PARLAY_THRESHOLD = 2;

  if (
    shouldCheckSport &&
    Object.values(eventCountPerSport).length < TOO_MANY_SPORTS_FOR_PARLAY_CARD
  ) {
    // We have at least one event that satisfies shouldCheckSport
    // and there are not too many different sports
    for (const sport in eventCountPerSport) {
      // Can we even show the Parlay Card for this sport?
      if (eventCountPerSport[sport] > NUM_OF_MATCHES_PARLAY_THRESHOLD) {
        // We finally check the sport and decide which reducer to return
        if (sport === SportEnum.FOOTBALL) {
          generateParlayReducer = generateWdwParlayReducer;
        } else {
          generateParlayReducer = generateWwParlayReducer;
        }
      }
    }
  } else {
    let numOfMatchesWithWdw = 0;
    let numOfMatchesWithWw = 0;

    // We do not have at least one event that satisfies shouldCheckSport
    // and we check to see which market is more present and try to use the corresponding reducer
    events &&
      Object.values(events).forEach(event => {
        event.markets.forEach(market => {
          if (markets[market].marketType === FULL_TIME_RESULT) {
            numOfMatchesWithWdw++;
          }
          if (markets[market].marketType === MATCH_WINNER) {
            numOfMatchesWithWw++;
          }
        });
      });

    if (numOfMatchesWithWdw >= numOfMatchesWithWw) {
      generateParlayReducer = generateWdwParlayReducer;
    } else {
      generateParlayReducer = generateWwParlayReducer;
    }
  }

  return generateParlayReducer;
};

interface parlayChecksType {
  tryDisplayingParlayCard: boolean;
  generateParlayReducer: (
    markets: Record<string, Market>
  ) => (accumulator: string[], currentValue: SportEvent) => string[];
}

const parlayChecks = (
  events: NormalizedEntity<SportEvent>,
  markets: Record<string, Market>
): parlayChecksType => {
  const eventCountPerSport = getEventCountPerSport(
    getAllSportsFromEvents(events)
  );

  const tryDisplayingParlayCard =
    !!(events && markets) && Object.values(eventCountPerSport).length !== 0;

  const generateParlayReducer = getParlayReducerGenerator(
    events,
    markets,
    eventCountPerSport
  );

  return {
    tryDisplayingParlayCard,
    generateParlayReducer
  };
};

function useDisplayEvents(
  betData: Ref<BetData>,
  eventCardOrderRef: Ref<CardTypes[]>
) {
  const eventCardOrder = computed(
    () => eventCardOrderRef.value || DEFAULT_EVENT_CARD_ORDER
  );

  const parlayMarkets = computed(() => {
    // DEV NOTE: total number of events per AdTag is 4
    // The further code restrictions and conditionals take this into account
    const NUM_OF_EVENTS_FOR_PARLAY_CARD = 3;
    const { tryDisplayingParlayCard, generateParlayReducer } = parlayChecks(
      betData.value?.events,
      betData.value?.markets
    );

    if (!tryDisplayingParlayCard) {
      return [];
    } else {
      return Object.values(betData.value.events)
        .reduce(generateParlayReducer(betData.value.markets), [])
        .slice(0, NUM_OF_EVENTS_FOR_PARLAY_CARD);
    }
  });

  /**
   * Mapping the event markets into their groups first to reduce repeated
   * operations of mapping markets.
   */
  const eventsWithGroupedMarkets = computed(() => {
    return (
      betData.value?.events &&
      Object.values(betData.value?.events).map(evnt => {
        // group event markets by card type

        const marketTypeGroups = getMarketsGroupedByType(
          evnt.markets,
          betData.value?.markets
        );

        // use only 1 list and promo market
        marketTypeGroups[LIST_GROUP]?.splice(1);
        marketTypeGroups[PROMO_GROUP]?.splice(1);
        // collate only the main market types

        const mainMarkets =
          marketTypeGroups[FTR_GROUP] &&
          getMainMarkets(
            marketTypeGroups[FTR_GROUP],
            betData.value?.markets,
            evnt.sport
          );

        // Display Main market card if more than 1 main market type exists.
        if (mainMarkets && mainMarkets.length > 1) {
          marketTypeGroups[MAIN_MARKET_GROUP] = mainMarkets;

          marketTypeGroups[INSIGHT_GROUP] = findMarketInsight(
            mainMarkets.map(marketId => betData.value.markets[marketId])
          );

          marketTypeGroups[FTR_GROUP] = undefined;
        } else {
          marketTypeGroups[FTR_GROUP]?.splice(1);
          const ftrMarketId = marketTypeGroups[FTR_GROUP]?.[0];

          if (
            ftrMarketId &&
            hasDrawOption(betData.value?.markets[ftrMarketId])
          ) {
            marketTypeGroups[WDW_GROUP] = [ftrMarketId];
          } else if (ftrMarketId) {
            marketTypeGroups[WW_GROUP] = [ftrMarketId];
          }
        }

        const result = Object.create(evnt);
        result.markets = marketTypeGroups;

        return result;
      })
    );
  });

  /**
   * Sorts the event by date order.
   * If an event is set to priority then it will display at start of list regardless of start time.
   * If the events have matching fixture start time, then the events are ordered by which contains a promo first.
   */
  const eventsOrderbyDate = computed(
    () =>
      eventsWithGroupedMarkets.value?.sort((a, b) => {
        // make the priority event first in the list
        if (a.priorityEvent) {
          return -1;
        }
        if (b.priorityEvent) {
          return 1;
        }

        if (new Date(a.fixture) < new Date(b.fixture)) {
          return -1;
        }
        if (new Date(a.fixture) > new Date(b.fixture)) {
          return 1;
        }

        if (
          a.markets[PROMO_GROUP]?.length === 1 &&
          !b.markets[PROMO_GROUP]?.length
        ) {
          return -1;
        }

        if (
          !a.markets[PROMO_GROUP]?.length &&
          b.markets[PROMO_GROUP]?.length === 1
        ) {
          return 1;
        }

        return 0;
      }) || []
  );

  type EventMarkets = Map<CardTypes, string[]>;

  /**
   * Ordering the markets for each of the ordered events
   */
  const orderedEvents = computed(() => {
    return eventsOrderbyDate.value.reduce((eventsAccumulator, currentEvent) => {
      // add events to map in the correct order
      const orderedEventsMarkets: EventMarkets = new Map();

      // Looping over the eventCard order to set the market cards
      // allows us to change that order more dynamically.
      eventCardOrder.value.forEach(cardType => {
        const eventMarketIds =
          currentEvent.markets[cardType] instanceof Array &&
          currentEvent.markets[cardType];

        if (eventMarketIds) orderedEventsMarkets.set(cardType, eventMarketIds);
      });

      if (orderedEventsMarkets.size > 0) {
        eventsAccumulator.push(orderedEventsMarkets);
      }

      return eventsAccumulator;
    }, [] as EventMarkets[]);
  });

  /**
   * flatten orderedEvents map to an array of arrays where
   * first item in array is type of market
   * and second item is array or market id
   *
   * [
   *  ['promo', [...marketIds]],
   *  ['mainMarket', [...marketIds]],
   *  ['insight', [...marketIds]],
   *  ['wdw', [...marketIds]],
   *
   *  ...
   *
   * ]
   * */

  const flattendOrderedEvents = computed(() => {
    return orderedEvents.value.reduce((acc, cur) => {
      acc.push(...cur.entries());
      return acc;
    }, [] as [CardTypes, Array<string>][]);
  });

  /**
   * Getting ids of the first 3 market cards displayed for analytics
   */
  const firstThreeMarketIds = computed(() => {
    return flattendOrderedEvents.value
      .map(market => {
        return market[1];
      })
      .flat(1)
      .slice(0, 3)
      .map(id => ({ betID: id }));
  });

  return {
    eventsOrderbyDate,
    orderedEvents,
    firstThreeMarketIds,
    parlayMarkets,
    flattendOrderedEvents
  };
}

export const DisplayEventsSymbol = Symbol("DisplayEventsSymbol");
export type DisplayEventsContext = ReturnType<typeof useDisplayEvents>;

export function provideDisplayEvents(
  betData: Ref<BetData>,
  eventCardOrder: Ref<CardTypes[]>
): void {
  provide(DisplayEventsSymbol, useDisplayEvents(betData, eventCardOrder));
}

export function injectDisplayEvents(): DisplayEventsContext {
  const events = inject<DisplayEventsContext>(DisplayEventsSymbol);

  if (!events) return {} as DisplayEventsContext;

  return events;
}
